/**
 * ExportModal Component
 *
 * Modal for exporting analytics data in various formats.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../base/Modal';
import { useAnalytics } from '../../hooks/useAnalytics';
import { exportAnalytics, type ExportFormat, type ExportData } from '../../utils/exportUtils';
import { latnLocale } from '../../utils/numberFormat';

export interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ExportModal({ isOpen, onClose }: ExportModalProps) {
  const { t, i18n } = useTranslation('dashboard');
  const isRTL = i18n.language === 'ar';
  const { overview, timeSeries, propertyAnalytics, customDateRange } = useAnalytics();
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('pdf');
  const [isExporting, setIsExporting] = useState(false);

  const formats: { id: ExportFormat; label: string; icon: string; description: string }[] = [
    {
      id: 'pdf',
      label: 'PDF',
      icon: 'ri-file-pdf-line',
      description: t('export.pdfDescription'),
    },
    {
      id: 'excel',
      label: 'Excel',
      icon: 'ri-file-excel-line',
      description: t('export.excelDescription'),
    },
    {
      id: 'csv',
      label: 'CSV',
      icon: 'ri-file-text-line',
      description: t('export.csvDescription'),
    },
  ];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Calculate date range for export
      const defaultStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const defaultEnd = new Date();
      const exportDateRange = customDateRange
        ? { start: new Date(customDateRange.start), end: new Date(customDateRange.end) }
        : { start: defaultStart, end: defaultEnd };

      const exportData: ExportData = {
        title: isRTL ? 'تقرير تحليلات صفقات 24' : 'Safaqat 24 Analytics Report',
        dateRange: exportDateRange,
        overview: {
          totalViews: overview?.totalViews || 0,
          totalClicks: overview?.totalClicks || 0,
          totalContacts: overview?.totalContacts || 0,
          totalSaves: overview?.totalSaves || 0,
          conversionRate: overview?.conversionRate || 0,
        },
        timeSeries: timeSeries.map((item) => ({
          date: item.date,
          views: item.views,
          clicks: item.clicks,
          contacts: item.contacts,
          saves: item.saves,
        })),
        propertyAnalytics: propertyAnalytics.map((prop) => ({
          propertyId: prop.propertyId,
          propertyTitle: prop.propertyTitle,
          views: prop.views,
          clicks: prop.clicks,
          contacts: prop.contacts,
          saves: prop.saves,
        })),
      };

      await exportAnalytics(exportData, selectedFormat, isRTL ? 'ar-SA' : 'en-US');
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--pv-color-primary)] flex items-center justify-center">
              <i className="ri-download-line text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--pv-color-text)]">
                {t('export.title')}
              </h2>
              <p className="text-sm text-[var(--pv-color-text-muted)]">
                {t('export.subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-[var(--pv-color-surface-soft)] rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl text-[var(--pv-color-text-muted)]"></i>
          </button>
        </div>

        {/* Format Selection */}
        <div className="space-y-3 mb-6">
          <label className="text-sm font-medium text-[var(--pv-color-text)]">
            {t('export.selectFormat')}
          </label>
          <div className="grid grid-cols-3 gap-3">
            {formats.map((format) => (
              <button
                key={format.id}
                onClick={() => setSelectedFormat(format.id)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  selectedFormat === format.id
                    ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-primary)]/5'
                    : 'border-[var(--pv-border-soft)] hover:border-[var(--pv-color-primary)]/50'
                }`}
              >
                <i
                  className={`${format.icon} text-2xl mb-2 block ${
                    selectedFormat === format.id
                      ? 'text-[var(--pv-color-primary)]'
                      : 'text-[var(--pv-color-text-muted)]'
                  }`}
                ></i>
                <span
                  className={`text-sm font-medium block ${
                    selectedFormat === format.id
                      ? 'text-[var(--pv-color-primary)]'
                      : 'text-[var(--pv-color-text)]'
                  }`}
                >
                  {format.label}
                </span>
              </button>
            ))}
          </div>
          <p className="text-xs text-[var(--pv-color-text-muted)] mt-2">
            {formats.find((f) => f.id === selectedFormat)?.description}
          </p>
        </div>

        {/* What's Included */}
        <div className="bg-gray-50 dark:bg-[var(--pv-color-surface-soft)] rounded-xl p-4 mb-6">
          <h4 className="text-sm font-medium text-[var(--pv-color-text)] mb-3">
            {t('export.includes')}
          </h4>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm text-[var(--pv-color-text-muted)]">
              <i className="ri-check-line text-green-500"></i>
              {t('export.includesSummary')}
            </li>
            <li className="flex items-center gap-2 text-sm text-[var(--pv-color-text-muted)]">
              <i className="ri-check-line text-green-500"></i>
              {t('export.includesTimeSeries')}
            </li>
            <li className="flex items-center gap-2 text-sm text-[var(--pv-color-text-muted)]">
              <i className="ri-check-line text-green-500"></i>
              {t('export.includesProperties')}
            </li>
          </ul>
        </div>

        {/* Date Range Info */}
        <div className="flex items-center gap-2 text-sm text-[var(--pv-color-text-muted)] mb-6">
          <i className="ri-calendar-line"></i>
          <span>
            {t('export.dateRange')}:{' '}
            {customDateRange
              ? `${new Date(customDateRange.start).toLocaleDateString(latnLocale(isRTL ? 'ar-SA' : 'en-US'))} - ${new Date(customDateRange.end).toLocaleDateString(latnLocale(isRTL ? 'ar-SA' : 'en-US'))}`
              : t('export.last30Days')}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="flex-1 py-3 px-4 bg-[var(--pv-color-primary)] text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                {t('export.exporting')}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-download-line"></i>
                {t('export.download')}
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
